import type { FormCategory, FormQuestion } from 'app/lib/forms/types'

export type FormTemplate = {
  key: string
  name: string
  description: string
  category: FormCategory
  questions: FormQuestion[]
}

/** Template question IDs are fixed strings, not generated, so a form created from a template
 *  keeps the same IDs everywhere and a mapping authored once travels with it. */
export const FORM_TEMPLATES: FormTemplate[] = [
  {
    key: `patient-registration`,
    name: `Patient Registration`,
    description: `Name, contact details, and address for a new patient.`,
    category: `registration`,
    questions: [
      { id: `q_r1a2b3`, kind: `shortText`, label: `First name`, required: true, mappingTarget: `firstName` },
      { id: `q_r2c4d5`, kind: `shortText`, label: `Last name`, required: true, mappingTarget: `lastName` },
      { id: `q_r3e6f7`, kind: `date`, label: `Date of birth`, required: true, mappingTarget: `birthDate` },
      { id: `q_r4g8h9`, kind: `phone`, label: `Cell phone`, required: true, mappingTarget: `cellPhone` },
      { id: `q_r5i0j1`, kind: `email`, label: `Email`, required: true, mappingTarget: `email` },
      { id: `q_r6k2l3`, kind: `address`, label: `Home address`, required: false, mappingTarget: `address` },
      {
        id: `q_r7m4n5`,
        kind: `singleSelect`,
        label: `How did you hear about us?`,
        required: false,
        options: [
          { id: `opt_search`, label: `Google or online search` },
          { id: `opt_friend`, label: `A friend or family member` },
          { id: `opt_insurance`, label: `My insurance directory` },
          { id: `opt_other`, label: `Somewhere else` },
        ],
      },
    ],
  },
  {
    key: `medical-history`,
    name: `Medical History`,
    description: `Conditions, medications, and allergies.`,
    category: `medicalHistory`,
    questions: [
      {
        id: `q_m1a2b3`,
        kind: `staticText`,
        label: `Your answers here help us treat you safely. Please answer as completely as you can.`,
        required: false,
      },
      {
        id: `q_m2c4d5`,
        kind: `yesNo`,
        label: `Are you currently taking any medications?`,
        required: true,
      },
      {
        id: `q_m3e6f7`,
        kind: `longText`,
        label: `Please list your medications`,
        required: true,
        showWhen: { questionId: `q_m2c4d5`, equals: `yes` },
      },
      {
        id: `q_m4g8h9`,
        kind: `yesNo`,
        label: `Do you have any allergies?`,
        required: true,
      },
      {
        id: `q_m5i0j1`,
        kind: `longText`,
        label: `Please list your allergies`,
        required: true,
        showWhen: { questionId: `q_m4g8h9`, equals: `yes` },
      },
      {
        id: `q_m6k2l3`,
        kind: `multiSelect`,
        label: `Have you been diagnosed with any of these?`,
        required: false,
        options: [
          { id: `opt_diabetes`, label: `Diabetes` },
          { id: `opt_heart`, label: `Heart condition` },
          { id: `opt_bp`, label: `High blood pressure` },
          { id: `opt_asthma`, label: `Asthma` },
          { id: `opt_bleeding`, label: `Bleeding disorder` },
        ],
      },
      {
        id: `q_m7m4n5`,
        kind: `longText`,
        label: `Anything else we should know?`,
        required: false,
      },
      { id: `q_m8o6p7`, kind: `signature`, label: `Signature`, required: true },
    ],
  },
  {
    key: `hipaa-acknowledgement`,
    name: `HIPAA Acknowledgement`,
    description: `Privacy practices acknowledgement. Replace the placeholder text with your practice's own reviewed wording.`,
    category: `consent`,
    questions: [
      {
        id: `q_h1a2b3`,
        kind: `staticText`,
        label: `REPLACE THIS TEXT. Paste your practice's own Notice of Privacy Practices acknowledgement here. Trackable does not supply legal wording.`,
        required: false,
      },
      {
        id: `q_h2c4d5`,
        kind: `yesNo`,
        label: `I acknowledge that I have received the Notice of Privacy Practices.`,
        required: true,
      },
      { id: `q_h3e6f7`, kind: `signature`, label: `Signature`, required: true },
    ],
  },
]

/** The order an office sends them in. Registration first because it is the easiest to answer,
 *  consent last because it is the one people stop and read. */
export const NEW_PATIENT_PACKET_TEMPLATE: string[] = [
  `patient-registration`,
  `medical-history`,
  `hipaa-acknowledgement`,
]
